// packages/frontend/src/features/recipes/InsightsStrip.tsx
//
// One muted line of cook-log-derived insights on the recipe detail view:
// "Cooked 5× · last made 12 days ago · usually ~40 min". Composed from
// whatever parts are available on `stats` — any missing/zero field is simply
// omitted, and the whole strip renders nothing when zero parts are available
// (e.g. a never-cooked recipe).
import type { JSX } from "react";
import { localDateInZone, type Stats } from "@recipevault/shared";
import { toDayNumber } from "../shelves/deriveShelves.js";

interface InsightsStripProps {
  stats: Stats | null;
  /**
   * Household-local YYYY-MM-DD "today", supplied by the caller (RecipeView,
   * via useHouseholdTimezone() + localDateInZone) rather than read from an
   * internal hook — keeps this component pure and testable without mocking
   * the auth/tz context.
   */
  today: string;
  /**
   * Household IANA timezone. `stats.lastCookedAt` is a UTC instant; this
   * converts it to the same local calendar day `today` is expressed in
   * before diffing, so "last made N days ago" is correct at day boundaries
   * regardless of the household's offset from UTC. Passed as a prop for the
   * same testability reason as `today`.
   */
  tzid: string;
}

/** "today" / "yesterday" / "N days ago" — never the awkward "0 days ago" or "1 days ago". */
function formatLastMade(daysAgo: number): string {
  if (daysAgo <= 0) return "last made today";
  if (daysAgo === 1) return "last made yesterday";
  return `last made ${daysAgo} days ago`;
}

export function InsightsStrip({ stats, today, tzid }: InsightsStripProps): JSX.Element | null {
  if (stats === null) return null;

  const parts: string[] = [];

  if (stats.cookCount > 0) parts.push(`Cooked ${stats.cookCount}×`);

  if (stats.lastCookedAt !== null) {
    const cookedDay = localDateInZone(tzid, new Date(stats.lastCookedAt));
    const daysAgo = toDayNumber(today) - toDayNumber(cookedDay);
    parts.push(formatLastMade(daysAgo));
  }

  if (stats.avgElapsedMin !== null) {
    parts.push(`usually ~${Math.round(stats.avgElapsedMin)} min`);
  }

  if (parts.length === 0) return null;

  return (
    <p className="text-sm text-zinc-500" data-testid="insights-strip">
      {parts.join(" · ")}
    </p>
  );
}
